import { useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import { useI18n } from '../../i18n';
import { BottleCap } from './BottleCap';
import { Icon } from './icons';

/**
 * Neutral shape for one predicted match, so the same row renders on both the
 * own-profile and public-profile lists — only the data source differs.
 */
export type PredRow = {
  matchNumber: number;
  capA?: string;
  flagA?: string;
  labelA: string;
  capB?: string;
  flagB?: string;
  labelB: string;
  status: 'upcoming' | 'live' | 'done' | 'cancelled';
  hidden?: boolean;
  predictedA?: number;
  predictedB?: number;
  actualA?: number;
  actualB?: number;
  points?: number;
  boost?: boolean;
  dateLabel?: string;
};

function isExact(r: PredRow): boolean {
  return (
    r.status === 'done' &&
    r.actualA != null &&
    r.predictedA != null &&
    r.actualA === r.predictedA &&
    r.actualB === r.predictedB
  );
}

/** One predicted match: caps, predicted score (with the real result under it
 *  once played), match label, boost badge, and points / LIVE. */
export function PredictionRow({ row }: { row: PredRow }) {
  const { t } = useI18n();
  const done = row.status === 'done';

  if (row.hidden) {
    return (
      <div class="predrow predrow--hidden" title={t('pubprofile.hidden')}>
        <span class="predrow__num bc-mono">#{row.matchNumber}</span>
        <BottleCap flag={row.flagA} code={row.capA} size={22} />
        <span class="predrow__pred bc-serif">? – ?</span>
        <BottleCap flag={row.flagB} code={row.capB} size={22} />
        <div class="predrow__info">
          <span class="predrow__teams">
            {row.labelA} – {row.labelB}
          </span>
          {row.dateLabel && <span class="predrow__date bc-mono">{row.dateLabel}</span>}
        </div>
        <span class="predrow__lock" aria-label={t('pubprofile.hidden')}>🔒</span>
      </div>
    );
  }

  const tone = !done
    ? 'pending'
    : isExact(row)
      ? 'correct'
      : (row.points ?? 0) > 0
        ? 'scored'
        : 'zero';

  return (
    <div class={`predrow predrow--${tone}`}>
      <span class="predrow__num bc-mono">#{row.matchNumber}</span>
      <BottleCap flag={row.flagA} code={row.capA} size={22} />
      <div class="predrow__scores">
        <span class="predrow__pred bc-serif">
          {row.predictedA ?? '?'} – {row.predictedB ?? '?'}
        </span>
        {done && row.actualA != null && (
          <span class="predrow__actual bc-mono">
            {row.actualA} – {row.actualB}
          </span>
        )}
      </div>
      <BottleCap flag={row.flagB} code={row.capB} size={22} />
      <div class="predrow__info">
        <span class="predrow__teams">
          {row.labelA} – {row.labelB}
        </span>
        {row.dateLabel && <span class="predrow__date bc-mono">{row.dateLabel}</span>}
      </div>
      {row.boost && <span class="predrow__boost bc-mono">2×</span>}
      {done ? (
        <span class="predrow__pts bc-mono">{row.points ?? 0} {t('game.points')}</span>
      ) : row.status === 'live' ? (
        <span class="predrow__live bc-mono">{t('game.status.live')}</span>
      ) : null}
    </div>
  );
}

/** Collapsible group header ("Active" / "Previous") with a count badge. */
export function PredictionGroup({
  label,
  count,
  defaultOpen = true,
  accent,
  children,
}: {
  label: string;
  count: number;
  defaultOpen?: boolean;
  accent?: 'mute';
  children: ComponentChildren;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div class="predgroup">
      <button
        class={`predgroup__head${accent === 'mute' ? ' predgroup__head--mute' : ''}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span class="predgroup__label">{label}</span>
        <span class="predgroup__count">{count}</span>
        <span class={`predgroup__chev${open ? ' is-open' : ''}`}>
          <Icon.chevron size={14} />
        </span>
      </button>
      {open && <div class="predgroup__body">{children}</div>}
    </div>
  );
}
